/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { HampState } from '../models/HampState';
import type { HampStrokeSettings } from '../models/HampStrokeSettings';
import type { HampVelocity } from '../models/HampVelocity';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HampService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current HAMP state of the device.
     * Get the current HAMP state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hamp">Handy Alternate Motion Protocol (HAMP)</a> for additional information about the HAMP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HAMP state.
     * @throws ApiError
     */
    public getHampState(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HampState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hamp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Set the HAMP state of the device.
     * Set the HAMP state of the device.
     *
     * Set both the velocity and stroke settings to update the HAMP state. If only one of the settings need to be updated, the other setting can be set to null to keep the current value.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hamp">Handy Alternate Motion Protocol (HAMP)</a> for additional information about the HAMP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HAMP state
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HAMP state.
     * @throws ApiError
     */
    public setHampState(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            velocity?: HampVelocity;
            stroke?: HampStrokeSettings;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HampState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hamp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Start the HAMP protocol.
     * Start the HAMP movement.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hamp">Handy Alternate Motion Protocol (HAMP)</a> for additional information about the HAMP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HAMP state.
     * @throws ApiError
     */
    public startHamp(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HampState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hamp/start',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Stop the HAMP protocol.
     * Stop the HAMP movement.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hamp">Handy Alternate Motion Protocol (HAMP)</a> for additional information about the HAMP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device response.
     * @throws ApiError
     */
    public stopHamp(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HampState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hamp/stop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Set the HAMP velocity.
     * Set the HAMP velocity.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hamp">Handy Alternate Motion Protocol (HAMP)</a> for additional information about the HAMP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HAMP velocity
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HAMP state.
     * @throws ApiError
     */
    public setHampVelocity(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            velocity: HampVelocity;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HampState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hamp/velocity',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set the HAMP stroke region.
     * Set the HAMP stroke region. The HAMP stroke region defines the movement range of the device within the current stroke region.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hamp">Handy Alternate Motion Protocol (HAMP)</a> for additional information about the HAMP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HAMP stroke settings
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HAMP state.
     * @throws ApiError
     */
    public setHampStroke(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: HampStrokeSettings,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HampState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hamp/stroke',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
